import { Button, Modal, Table, toast } from '@heroui/react';
import * as React from 'react';
import { IconEye, IconFolderPlus, IconPlus, IconRefresh } from '@tabler/icons-react';
import { useAppSelector } from '@/stores/hooks';
import { trpc, trpcClient } from '@/lib/trpc';
import { CategoryForm, type CategoryFormPayload } from './components/CategoryForm';
import { QUESTIONNAIRE_CATEGORY_TYPE } from './components/constants';
import { QuestionnaireFormDialog, type QuestionnaireFormDialogQuestionnaire } from './components/QuestionnaireFormDialog';
import { QuizRenderer } from './components/QuizRenderer';
import type {
  QuestionnaireSchema,
  QuestionnaireType,
  QuestionnaireSettings,
  QuestionnaireFormData,
} from './components/types';
import { createDefaultSettings } from './components/types';
import {
  detailStructureToQuestionnaireSchema,
  formDataToCreateStructure,
  formTypeToScene,
  sceneToFormType,
} from './components/qnaire-api-adapter';
import { tryBuildStructurePatchOps } from './components/qnaire-structure-patch-builder';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

type CategoryTreeNode = {
  id: string;
  name?: string | null;
  children?: CategoryTreeNode[];
};

function flattenCategoryNames(nodes: CategoryTreeNode[], prefix: string[] = []): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = [];
  for (const n of nodes) {
    const parts = [...prefix, n.name || '未命名'];
    out.push({ id: n.id, label: parts.join(' / ') });
    if (n.children?.length) {
      out.push(...flattenCategoryNames(n.children, parts));
    }
  }
  return out;
}

function CategoryTree({
  nodes,
  selectedId,
  depth,
  onSelect,
}: {
  nodes: CategoryTreeNode[];
  selectedId: string | null;
  depth: number;
  onSelect: (id: string | null) => void;
}) {
  return (
    <ul className={depth === 0 ? 'space-y-1' : 'ml-3 mt-1 space-y-1 border-l border-divider pl-2'}>
      {depth === 0 ? (
        <li>
          <button
            type="button"
            onClick={() => onSelect(null)}
            className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${selectedId == null ? 'bg-accent font-medium' : 'hover:bg-muted'}`}
          >
            全部问卷
          </button>
        </li>
      ) : null}
      {nodes.map((n) => (
        <li key={n.id}>
          <button
            type="button"
            onClick={() => onSelect(n.id)}
            className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${selectedId === n.id ? 'bg-accent font-medium' : 'hover:bg-muted'}`}
          >
            {n.name || '未命名'}
          </button>
          {n.children && n.children.length > 0 ? (
            <CategoryTree nodes={n.children} selectedId={selectedId} depth={depth + 1} onSelect={onSelect} />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

const SCENE_LABEL: Record<string, string> = {
  survey: '调查',
  evaluation: '评估',
  quiz: '测验',
  vote: '投票',
};

const STATUS_LABEL: Record<number, string> = {
  0: '草稿',
  1: '已发布',
  2: '已关闭',
};

function mergeQuestionnaireSettings(
  base: QuestionnaireSettings,
  raw: unknown
): QuestionnaireSettings {
  if (!raw || typeof raw !== 'object') return base;
  return { ...base, ...(raw as Record<string, unknown>) } as QuestionnaireSettings;
}

export default function QnairesPage() {
  const user = useAppSelector((s) => s.auth.user);
  const spaceId = user?.spaces?.[0]?.id ?? null;
  const creatorId = user?.id ?? null;

  const [selectedCategoryId, setSelectedCategoryId] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const pageSize = 20;

  const [categoryFormOpen, setCategoryFormOpen] = React.useState(false);
  const [editingCategory, setEditingCategory] = React.useState<CategoryFormPayload | null>(null);

  const [formOpen, setFormOpen] = React.useState(false);
  const [formQuestionnaire, setFormQuestionnaire] = React.useState<QuestionnaireFormDialogQuestionnaire | null>(null);
  const [formLoading, setFormLoading] = React.useState(false);

  const [preview, setPreview] = React.useState<{
    title: string;
    schema: QuestionnaireSchema | Record<string, unknown> | null;
  } | null>(null);

  const treeQuery = trpc.categories.getCategoryTree.useQuery(
    { scope: 'MINE', type: QUESTIONNAIRE_CATEGORY_TYPE },
    { staleTime: 30_000 }
  );

  const listQuery = trpc.qnaires.list.useQuery(
    {
      page,
      pageSize,
      ...(selectedCategoryId ? { categoryId: selectedCategoryId } : {}),
      sortOrder: 'desc',
    },
    { staleTime: 10_000 }
  );

  const createCategory = trpc.categories.create.useMutation({
    onSuccess: () => {
      void treeQuery.refetch();
    },
  });
  const updateCategory = trpc.categories.update.useMutation({
    onSuccess: () => {
      void treeQuery.refetch();
    },
  });
  const createQ = trpc.qnaires.create.useMutation({
    onSuccess: () => {
      void listQuery.refetch();
    },
  });
  const updateQ = trpc.qnaires.update.useMutation({
    onSuccess: () => {
      void listQuery.refetch();
    },
  });

  const tree = (treeQuery.data ?? []) as CategoryTreeNode[];
  const flatNames = React.useMemo(() => flattenCategoryNames(tree), [tree]);
  const selectedLabel = React.useMemo(() => {
    if (!selectedCategoryId) return '全部问卷';
    return flatNames.find((f) => f.id === selectedCategoryId)?.label ?? '分类';
  }, [flatNames, selectedCategoryId]);

  React.useEffect(() => {
    setPage(1);
  }, [selectedCategoryId]);

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const openCreate = () => {
    setFormQuestionnaire(null);
    setFormOpen(true);
  };

  const openEdit = async (id: string) => {
    setFormLoading(true);
    try {
      const row = await trpcClient.qnaires.get.query({ id, withAll: true });
      const baseSettings = createDefaultSettings();
      const settings = mergeQuestionnaireSettings(baseSettings, row.settings);
      const schema = detailStructureToQuestionnaireSchema(
        row.structure as Parameters<typeof detailStructureToQuestionnaireSchema>[0],
        settings
      );
      setFormQuestionnaire({
        id: row.id ?? undefined,
        title: row.title ?? '',
        description: row.description ?? undefined,
        type: sceneToFormType(row.scene),
        isShared: Boolean((row.settings as { isShared?: boolean } | null)?.isShared),
        schemaJson: schema as unknown as Record<string, unknown>,
      });
      setFormOpen(true);
    } catch (e) {
      console.error(e);
      toast.danger('加载问卷失败');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCategorySubmit = async (payload: CategoryFormPayload) => {
    try {
      if (payload.id) {
        await updateCategory.mutateAsync({ id: payload.id, data: { name: payload.name } });
        toast.success('分类已更新');
      } else {
        await createCategory.mutateAsync({
          name: payload.name,
          type: QUESTIONNAIRE_CATEGORY_TYPE,
        });
        toast.success('分类已创建');
      }
    } catch (e) {
      console.error(e);
      toast.danger('分类保存失败');
    }
  };

  const handleFormSubmit = async (payload: {
    title: string;
    description?: string;
    type: QuestionnaireType;
    is_shared: boolean;
    schema_json: QuestionnaireSchema;
    formData: QuestionnaireFormData;
    structureBaseline: QuestionnaireFormData | null;
  }) => {
    try {
      setFormLoading(true);
      if (!spaceId || !creatorId) {
        toast.danger('请先登录并确保当前空间可用');
        return;
      }
      const scene = formTypeToScene(payload.type);
      const settingsPayload = {
        ...(payload.formData.settings as unknown as Record<string, unknown>),
        ...(payload.is_shared !== undefined ? { isShared: payload.is_shared } : {}),
      };

      if (formQuestionnaire?.id) {
        const qid = formQuestionnaire.id;
        const patch = tryBuildStructurePatchOps(payload.structureBaseline, payload.formData, qid);
        if (!patch.ok) {
          toast.danger(patch.message);
          return;
        }
        if (patch.ops.length > 0) {
          await trpcClient.qnaires.applyStructurePatch.mutate({
            qnaireId: qid,
            ops: patch.ops as never[],
          });
        }
        await updateQ.mutateAsync({
          id: qid,
          data: {
            title: payload.title,
            description: payload.description ?? null,
            scene,
            settings: settingsPayload,
          },
        });
        toast.success('问卷已更新');
      } else {
        const structure = formDataToCreateStructure(payload.formData);
        const created = await createQ.mutateAsync({
          title: payload.title,
          description: payload.description ?? null,
          scene,
          status: 0,
          settings: settingsPayload,
          spaceId,
          creatorId,
          structure,
        });
        if (selectedCategoryId && created?.id) {
          await trpcClient.qnaireCategories.add.mutate({
            qnaireId: created.id,
            categoryId: selectedCategoryId,
          });
        }
        toast.success('问卷已创建');
      }
    } catch (e) {
      console.error(e);
      toast.danger('保存失败');
    } finally {
      setFormLoading(false);
    }
  };

  const openPreview = async (id: string, title: string) => {
    try {
      const row = await trpcClient.qnaires.get.query({ id, withAll: true });
      const baseSettings = createDefaultSettings();
      const settings = mergeQuestionnaireSettings(baseSettings, row.settings);
      const schema = detailStructureToQuestionnaireSchema(
        row.structure as Parameters<typeof detailStructureToQuestionnaireSchema>[0],
        settings
      );
      setPreview({
        title: title || row.title || '问卷预览',
        schema: schema as QuestionnaireSchema | null,
      });
    } catch (e) {
      console.error(e);
      toast.danger('无法加载预览');
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full bg-background">
      <aside className="flex w-64 shrink-0 flex-col border-r border-divider">
        <div className="border-b border-divider p-3">
          <div className="text-xs font-medium text-muted-foreground">问卷分类</div>
          <div className="mt-1 text-sm font-semibold">我的目录</div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {treeQuery.isLoading ? (
            <div className="p-2 text-sm text-muted-foreground">加载中…</div>
          ) : tree.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground">暂无分类，请先新建</div>
          ) : (
            <CategoryTree nodes={tree} selectedId={selectedCategoryId} depth={0} onSelect={setSelectedCategoryId} />
          )}
        </div>
        <div className="border-t border-divider p-2">
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onPress={() => {
              setEditingCategory(null);
              setCategoryFormOpen(true);
            }}
          >
            <IconFolderPlus className="mr-1 size-4" />
            新建分类
          </Button>
        </div>
      </aside>

      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-divider p-4">
          <div>
            <h1 className="text-xl font-semibold">问卷</h1>
            <p className="text-sm text-muted-foreground">
              {selectedLabel}
              {total ? ` · 共 ${total} 条` : null}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="primary" onPress={openCreate}>
              <IconPlus className="mr-1 size-4" />
              新建问卷
            </Button>
            <Button size="sm" variant="ghost" onPress={() => void listQuery.refetch()} isDisabled={listQuery.isFetching}>
              <IconRefresh className="mr-1 size-4" />
              刷新
            </Button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          <Table>
            <Table.ScrollContainer className="max-h-[min(60vh,560px)]">
              <Table.Content aria-label="问卷列表">
                <Table.Header>
                  <Table.Column id="title" isRowHeader className="min-w-0">
                    标题
                  </Table.Column>
                  <Table.Column id="scene" className="w-28">
                    类型
                  </Table.Column>
                  <Table.Column id="status" className="w-28">
                    状态
                  </Table.Column>
                  <Table.Column id="actions" className="w-52 text-end">
                    操作
                  </Table.Column>
                </Table.Header>
                <Table.Body>
                  {listQuery.isLoading ? (
                    <Table.Row key="__loading__" id="__loading__">
                      <Table.Cell colSpan={4} className="text-center text-muted-foreground">
                        加载中…
                      </Table.Cell>
                    </Table.Row>
                  ) : items.length === 0 ? (
                    <Table.Row key="__empty__" id="__empty__">
                      <Table.Cell colSpan={4} className="text-center text-muted-foreground">
                        暂无问卷
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    items.map((row) => {
                      const r = row as {
                        id?: string;
                        title?: string | null;
                        scene?: string | null;
                        status?: number;
                      };
                      const rid = r.id;
                      if (!rid) return null;
                      return (
                        <Table.Row key={rid} id={rid}>
                          <Table.Cell className="font-medium">{r.title || '-'}</Table.Cell>
                          <Table.Cell>{SCENE_LABEL[r.scene || ''] || r.scene || '-'}</Table.Cell>
                          <Table.Cell>{STATUS_LABEL[r.status ?? 0] ?? String(r.status ?? '')}</Table.Cell>
                          <Table.Cell className="text-end">
                            <Button size="sm" variant="ghost" onPress={() => void openPreview(rid, row.title || '')}>
                              <IconEye className="mr-1 size-4" />
                              预览
                            </Button>
                            <Button size="sm" variant="secondary" onPress={() => void openEdit(rid)}>
                              编辑
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })
                  )}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>

          {totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                isDisabled={page <= 1}
                onPress={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </Button>
              <span className="text-sm text-muted-foreground">
                {page} / {totalPages}
              </span>
              <Button
                size="sm"
                variant="secondary"
                isDisabled={page >= totalPages}
                onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                下一页
              </Button>
            </div>
          ) : null}
        </div>
      </section>

      <CategoryForm
        open={categoryFormOpen}
        onOpenChange={setCategoryFormOpen}
        category={editingCategory}
        loading={createCategory.isPending || updateCategory.isPending}
        onSubmit={handleCategorySubmit}
      />

      <QuestionnaireFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        questionnaire={formQuestionnaire}
        loading={formLoading || createQ.isPending || updateQ.isPending}
        onSubmit={handleFormSubmit}
      />

      <Modal isOpen={Boolean(preview)} onOpenChange={(next) => !next && setPreview(null)}>
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" scroll="inside" size="full" className="max-w-3xl">
            <Modal.Dialog className="max-h-[90vh]">
              <Modal.Header>
                <Modal.Heading>预览 · {preview?.title}</Modal.Heading>
              </Modal.Header>
              <Modal.Body className="overflow-y-auto">
                {preview ? (
                  <QuizRenderer schema={preview.schema} title={preview.title} mode="trial" onFinish={() => {}} />
                ) : null}
              </Modal.Body>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </div>
  );
}
